<template>
  <div :id="galleryID" class="gallery">
    <a
      v-for="(item, index) in imagesData"
      :key="item.largeURL || item.videoURL"
      :href="item.type === 'video' ? item.videoURL : item.largeURL"
      :data-type="item.type"
      :data-video="item.videoURL"
      :data-pswp-width="item.width"
      :data-pswp-height="item.height"
    >
      <div
        class="thumbnail-container"
        :style="{ aspectRatio: `${item.width} / ${item.height}` }"
      >
        <img
          class="thumbnail-img"
          :class="{ 'is-loaded': loadedImages.has(itemKey(item)) }"
          :style="{ '--animation-delay': `${index * 0.05}s` }"
          :src="item.type === 'video' ? item.poster : item.thumbnailURL"
          :width="item.width"
          :height="item.height"
          :alt="`Full size artwork by ${item.artist?.name ?? 'unknown artist'}, ${item.creationDate}`"
          loading="lazy"
          decoding="async"
          @load="loadedImages.add(itemKey(item))"
        />
        <div v-if="item.type === 'video'" class="video-play-icon">
          <svg
            viewBox="0 0 24 24"
            width="30"
            height="30"
            fill="white"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
        <div class="overlay" aria-hidden="true">
          {{ item.artist?.name }} {{ item.creationDate }}
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import { computed, reactive } from "vue";
import { useStore } from "vuex";
import {
  activeImageFilter,
  imageFilters,
  tagFilters,
} from "@/assets/filters";
import { socialMedia } from "@/assets/social_media";
import { toSafeSvg, escapeHtml } from "@/utils/safeMarkup";

// Swipe arrow SVG
const leftArrowSVGString = `<svg class="pswp__icn" height="800px" width="800px" viewBox="0 0 55.752 55.752">
	<path class="arrow-svg" d="M12.745,23.915c0.283-0.282,0.59-0.52,0.913-0.727L35.266,1.581c2.108-2.107,5.528-2.108,7.637,0.001
		c2.109,2.108,2.109,5.527,0,7.637L24.294,27.828l18.705,18.706c2.109,2.108,2.109,5.526,0,7.637
		c-1.055,1.056-2.438,1.582-3.818,1.582s-2.764-0.526-3.818-1.582L13.658,32.464c-0.323-0.207-0.632-0.445-0.913-0.727
		c-1.078-1.078-1.598-2.498-1.572-3.911C11.147,26.413,11.667,24.994,12.745,23.915z"/>
</svg>`;

export default {
  name: "SimpleGallery",
  props: {
    galleryID: {
      type: String,
      required: true,
    },
    images: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStore();

    // Keyed by image identity instead of by index: the year filter changes the
    // rendered list, so index keys would leak the loaded state between images
    const loadedImages = reactive(new Set());

    const itemKey = (item) => item.largeURL || item.videoURL;

    const availableTags = computed(
      () => new Set(props.images.flatMap((image) => image.tags ?? []))
    );

    // Every active filter becomes a predicate, and an image has to satisfy all of
    // them. Predicates are rebuilt from scratch when the selection changes, so
    // nothing is left behind from a previous filter
    const activeFilters = computed(() => {
      const selected = store.state.filters;

      const byImageValue = imageFilters
        .map((filter) => ({
          filter,
          value: activeImageFilter(props.images, filter, selected[filter.key]),
        }))
        .filter(({ value }) => value !== null)
        .map(({ filter, value }) => (image) => filter.get(image) === value);

      // One tag per facet, and the same rule as above: a tag this gallery does
      // not have is ignored instead of emptying it
      const byTag = tagFilters.flatMap((facet) => {
        const tag = facet.tags.find(
          (candidate) => selected[facet.key] === candidate
        );
        return tag && availableTags.value.has(tag)
          ? [(image) => image.tags?.includes(tag)]
          : [];
      });

      return [...byImageValue, ...byTag];
    });

    const imagesData = computed(() =>
      [...props.images]
        .filter((image) =>
          activeFilters.value.every((matches) => matches(image))
        )
        .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate))
    );

    return {
      imagesData,
      itemKey,
      loadedImages,
    };
  },
  mounted() {
    const registerSocialMediaButtons = (lightbox) => {
      const currentImageData = this.imagesData[lightbox.pswp.potentialIndex];
      if (!currentImageData || !currentImageData.artist) {
        return;
      }

      registerArtistName(lightbox, currentImageData.artist.name);

      for (const artistSM of currentImageData.artist.socialMedia) {
        const network = Object.keys(artistSM)[0];
        const url = Object.values(artistSM)[0];

        lightbox.pswp.ui.registerElement({
          name: "social-media-button",
          ariaLabel: `Open ${currentImageData.artist.name} on ${network}`,
          order: 9,
          isButton: true,
          html: getSvg(network),
          onClick: () => {
            // noopener/noreferrer: without them the opened page gets a
            // window.opener reference and can navigate this tab away
            window.open(url, "_blank", "noopener,noreferrer");
          },
        });
      }
    };

    function registerArtistName(lightbox, artistName) {
      lightbox.pswp.ui.registerElement({
        name: "artist-name",
        ariaLabel: `By ${artistName}`,
        order: 1,
        onInit: (el) => {
          el.innerText = "By " + artistName + ":";
        },
      });
    }

    function getSvg(socialMediaName) {
      return toSafeSvg(socialMedia[socialMediaName]?.svg);
    }

    function unregisterSocialMediaButtons() {
      const pswpContainer = document.querySelector(".pswp");

      if (pswpContainer) {
        const elements = pswpContainer.querySelectorAll(
          `.pswp__button--social-media-button`
        );

        if (elements.length > 0) {
          elements.forEach((element) => {
            element.remove();
          });
        }
      }
    }

    function unregisterArtistName() {
      const pswpContainer = document.querySelector(".pswp");

      if (pswpContainer) {
        const elements = pswpContainer.querySelectorAll(`.pswp__artist-name`);

        if (elements.length > 0) {
          elements.forEach((element) => {
            element.remove();
          });
        }
      }
    }

    if (!this.lightbox) {
      this.lightbox = new PhotoSwipeLightbox({
        arrowPrevSVG: leftArrowSVGString,
        arrowNextSVG: leftArrowSVGString,
        gallery: "#" + this.$props.galleryID,
        children: "a",
        pswpModule: () => import("photoswipe"),
        zoom: false, // Hide zoom button (wheel zoom stays available via wheelToZoom)
        counter: false, // Hide slide counter
        bgOpacity: 0.9, // Set background opacity
        loop: false,
        wheelToZoom: true,
        allowPanToNext: false,
        padding: { top: 60, bottom: 0, left: 0, right: 0 },
        mainClass: "pswp--custom-icon-colors", // Apply custom styles to this instance
      });
      this.lightbox.on("change", () => {
        // triggers when slide is switched, and at initialization
        unregisterArtistName();
        unregisterSocialMediaButtons();
        registerSocialMediaButtons(this.lightbox);
        // Espera a que PhotoSwipe renderice el nuevo slide
        requestAnimationFrame(() => {
          // Pausar todos los vídeos
          document.querySelectorAll(".pswp video").forEach((video) => {
            video.pause();
            video.currentTime = 0;
          });

          // Reproducir el vídeo del slide activo (si lo hay)
          const activeVideo = document.querySelector(
            ".pswp__item--current video"
          );

          if (activeVideo) {
            activeVideo.play().catch(() => {
              // Algunos navegadores bloquean autoplay con sonido
            });
          }
        });
      });
      this.lightbox.addFilter("domItemData", (itemData, element) => {
        if (element.dataset.type === "video") {
          return {
            html: `
        <div class="pswp-video-container">
          <div class="video-play-overlay">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,.45)" />
              <path d="M9 7.5L17 12L9 16.5Z" fill="white"/>
            </svg>
          </div>

          <video controls playsinline preload="metadata" loop>
            <source src="${escapeHtml(element.dataset.video)}" type="video/mp4">
          </video>
        </div>
      `,
          };
        }

        return itemData;
      });
      this.lightbox.on("contentActivate", ({ content }) => {
        if (content.type !== "html") return;

        const video = content.element?.querySelector("video");

        if (video) {
          video.play().catch(() => {
            // El navegador puede bloquear el autoplay si el vídeo tiene sonido
          });
        }
      });

      this.lightbox.on("contentDeactivate", ({ content }) => {
        if (content.type !== "html") return;

        const video = content.element?.querySelector("video");

        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });

      this.lightbox.on("contentAppend", ({ content }) => {
        const video = content.element?.querySelector("video");
        const overlay = content.element?.querySelector(".video-play-overlay");

        if (!video || !overlay) return;

        const updateOverlay = () => {
          if (video.paused) {
            overlay.classList.remove("hidden");
          } else {
            overlay.classList.add("hidden");
          }
        };

        video.addEventListener("play", updateOverlay);
        video.addEventListener("pause", updateOverlay);

        // Comprobar el estado inicial
        requestAnimationFrame(updateOverlay);
      });

      this.lightbox.init();
    }
  },
  unmounted() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  },
  methods: {},
};
</script>

<style lang="less" scoped>
@gold: #bfa181;

.gallery a {
  margin-right: 10px;
  margin-bottom: 10px;
}

.gallery {
  display: flex;
  flex-wrap: wrap;
  align-items: center; /* Centra verticalmente */
  justify-content: center; /* Centra horizontalmente */
  width: 100%;
  height: 100%;
  z-index: 1000; /* Asegura que esté por encima de otros elementos */
  padding: 5%;
  padding-top: 1%;
}

.thumbnail-container {
  position: relative;
  width: 100%;
}

.video-play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  width: 150px;
  height: 150px;
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, 0.55);

  pointer-events: none;
  z-index: 2;
}

.video-play-icon svg {
  width: 100px;
  height: 100px;
}

.thumbnail-img {
  display: block;
  width: 100%;
  height: auto;
  opacity: 0;
}

.thumbnail-img.is-loaded {
  animation: fadeIn 2s var(--animation-delay) both;
}

.overlay {
  position: absolute;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5); /* Black see-through */
  color: @gold;
  width: 100%;
  transition: 0.5s ease;
  opacity: 0;
  font-size: 20px;
  padding: 20px;
  text-align: center;
}

.thumbnail-container:hover .overlay {
  opacity: 1;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .thumbnail-img {
    animation: none;
  }

  .overlay {
    transition: none;
  }
}

@media (max-width: 600px) {
  .thumbnail-container {
    width: 100%; /* Asegura que el contenedor se ajuste al 100% del ancho de la pantalla */
    overflow: hidden; /* Evita que el contenido desborde */
  }

  .thumbnail-img {
    width: 100%; /* Ajusta el ancho de la imagen al contenedor */
    height: auto; /* Mantiene la proporción de la imagen */
    object-fit: cover; /* Cubre el área del contenedor sin distorsionarse */
  }
}
</style>

<style lang="less">
@gold: #bfa181;

/* PhotoSwipe renders every rule below into its own DOM, either in its default
   UI or through the `html` strings registered in the script above. None of
   those elements belong to this component's template, so they never receive a
   scope attribute and these selectors must stay global. */

button.pswp__button {
  display: flex; /* Enable Flexbox */
  align-items: center; /* Center vertically */
  justify-content: center; /* Center horizontally */
}

button.pswp__button--social-media-button svg {
  width: 75%; /* Scale SVG to fit within button */
  height: 75%; /* Maintain aspect ratio */
}

.social-media-svg {
  transform-box: fill-box;
  transform-origin: center center;
  transform: scale(0.8);
}

.pswp__button--close {
  order: 99; /* Ensure the close button appears last */
}

.pswp--custom-icon-colors {
  --pswp-icon-color: @gold;
  --pswp-icon-color-secondary: #333;
}

.pswp__top-bar {
  font-family: "Dragon Fire", sans-serif;
  color: @gold;
}

.pswp__artist-name {
  display: flex;
  font-size: 30px;
  align-items: center; /* Centra verticalmente */
  justify-content: center; /* Centra horizontalmente */
  padding-right: 10px;
}

.pswp-video-container {
  position: relative;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
}

.pswp-video-container video {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.video-play-overlay {
  position: absolute;
  inset: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  pointer-events: none;
  transition: opacity 0.2s ease;
}

.video-play-overlay svg {
  width: 110px;
  height: 110px;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.5));
}

.video-play-overlay.hidden {
  opacity: 0;
}

@media (max-width: 600px) {
  .pswp__artist-name {
    display: flex;
    font-size: 20px;
    align-items: center; /* Centra verticalmente */
    justify-content: center; /* Centra horizontalmente */
    padding-right: 10px;
  }

  .pswp__button--social-media-button {
    width: 35px;
  }

  .arrow-svg {
    visibility: hidden;
  }
}
</style>
